import type { DocumentRevision } from '@/entities/Document';
import { membersApi } from '@/entities/Branch';
import type { Position } from '@/entities/User';
import api from '@/shared/api';
import type { ApiResponse } from '@/shared/lib';

interface ApprovalSessionPayload {
  trainingDate: string;
  responsibleMemberId: number;
  responsibleUserPositionId: number;
}

interface ApproveDocumentPayload {
  documentRevisionId: number;
  trainingJustification?: string;
  notes?: string;
  users?: number[];
  positions?: number[];
  positionId?: number;
  sessions?: ApprovalSessionPayload[];
}

interface RejectDocumentPayload {
  documentRevisionId: number;
  justification: string;
  positionId?: number;
}

/**
 * A decisão é um recurso do próprio Registro, criado em `/matrices/:id/approval`: o desfecho vai no
 * corpo, e não no caminho. O cargo é obrigatório para aprovar e a tela não o oferece ao reprovar.
 */
interface MatrixApprovalPayload {
  matrixId: number;
  status: 'APPROVED' | 'REJECTED';
  positionId?: number;
  justification?: string;
}

type ApprovalMutationResponse = Record<string, unknown>;

class ApprovalApi {
  async getAwaitingApproval(): Promise<DocumentRevision[]> {
    const response = await api.get('/document-revisions/awaiting-approval');
    return response.data.data || [];
  }

  async getAwaitingApprovalById(revisionId: string | number): Promise<ApiResponse<DocumentRevision>> {
    const response = await api.get(`/document-revisions/awaiting-approval/${revisionId}`);
    return response.data as ApiResponse<DocumentRevision>;
  }

  async getPositions(): Promise<ApiResponse<Position[]>> {
    const response = await api.get('/positions');
    return response.data as ApiResponse<Position[]>;
  }

  async getMemberPositions(memberId: number): Promise<Position[]> {
    try {
      const member = await membersApi.getById(memberId);
      return (member.positions ?? []).map((position) => ({
        id: position.id,
        name: position.name,
      }));
    } catch {
      return [];
    }
  }

  async approveDocumentWithSessions(
    payload: ApproveDocumentPayload
  ): Promise<ApprovalMutationResponse> {
    const response = await api.post('document-approvals/approve', payload);
    return response.data as ApprovalMutationResponse;
  }

  async rejectDocumentWithPosition(
    payload: RejectDocumentPayload
  ): Promise<ApprovalMutationResponse> {
    const response = await api.post('document-approvals/reject', payload);
    return response.data as ApprovalMutationResponse;
  }

  async decideMatrixApproval({
    matrixId,
    ...decision
  }: MatrixApprovalPayload): Promise<ApprovalMutationResponse> {
    const response = await api.post(`/matrices/${matrixId}/approval`, decision);
    return response.data as ApprovalMutationResponse;
  }
}

export const approvalApi = new ApprovalApi();
